import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { PerformanceMetric, EditingWeek, EditingCell } from '../interfaces/statistics.interface';

/**
 * EditingManagerService
 * 
 * A StatisticsTableComponent szerkesztési logikájáért felelős service.
 * Kezeli a cellák és hetek szerkesztési állapotait, valamint a kapcsolódó műveleteket.
 */

@Injectable()
export class EditingManagerService {

  // Szerkesztési állapot változók
  private _editingWeek = new BehaviorSubject<EditingWeek | null>(null);
  private _editingCell = new BehaviorSubject<EditingCell | null>(null);
  private _editingValues = new BehaviorSubject<{[metricIndex: number]: number[]}>({});
  private _editingDayIndices = new BehaviorSubject<number[]>([]);
  private _singleCellEditingValue = new BehaviorSubject<number>(0);

  // Publikus observables az állapot megfigyelésére
  public readonly editingWeek$ = this._editingWeek.asObservable();
  public readonly editingCell$ = this._editingCell.asObservable();
  public readonly editingValues$ = this._editingValues.asObservable();
  public readonly editingDayIndices$ = this._editingDayIndices.asObservable();
  public readonly singleCellEditingValue$ = this._singleCellEditingValue.asObservable();

  constructor() {}

  /**
   * Jelenlegi szerkesztési állapotok getter-ei
   */
  get currentEditingWeek(): EditingWeek | null {
    return this._editingWeek.value;
  }

  get currentEditingCell(): EditingCell | null {
    return this._editingCell.value;
  }

  get currentEditingValues(): {[metricIndex: number]: number[]} {
    return this._editingValues.value;
  }

  get currentEditingDayIndices(): number[] {
    return this._editingDayIndices.value;
  }

  get currentSingleCellEditingValue(): number {
    return this._singleCellEditingValue.value;
  }

  /**
   * Ellenőrzi, hogy egy adott cella szerkeszthető-e
   */
  isEditableCell(metricIndex: number, rowType: 'actual' | 'plan', isKumMode: boolean, metrics: PerformanceMetric[]): boolean {
    const metricName = metrics[metricIndex]?.name;
    if (isKumMode) {
      // KÜM módban minden metrika szerkeszthető mind plan és actual soraiban
      return true;
    } else {
      // Call Center módban az Indított hívás, Egyeztetett időpont és Vagyonra egyeztetett
      // metrikák mind a plan, mind az actual soraiban szerkeszthetők
      return (metricName === 'Indított hívás' || metricName === 'Egyeztetett időpont' || metricName === 'Vagyonra egyeztetett');
    }
  }

  /**
   * Ellenőrzi, hogy egy adott cella szerkeszthető-e az adott napon
   */
  isCellEditableForDay(
    metricIndex: number, 
    rowType: 'actual' | 'plan', 
    dayIndex: number,
    isKumMode: boolean,
    metrics: PerformanceMetric[],
    isWeekendFn: (dayIndex: number) => boolean
  ): boolean {
    return this.isEditableCell(metricIndex, rowType, isKumMode, metrics) && !isWeekendFn(dayIndex);
  }

  /**
   * Ellenőrzi, hogy egy adott cella éppen szerkesztés alatt áll-e
   */
  isEditing(metricIndex: number, rowType: 'actual' | 'plan', dayIndex: number, isWeekendFn: (dayIndex: number) => boolean): boolean {
    // Hétvégén nincs szerkesztés
    if (isWeekendFn(dayIndex)) {
      return false;
    }
    
    // Egyes cella szerkesztés ellenőrzése (actual értékek)
    const editingCell = this.currentEditingCell;
    if (editingCell &&
        editingCell.metricIndex === metricIndex &&
        editingCell.rowType === rowType &&
        editingCell.dayIndex === dayIndex) {
      return true;
    }
    
    // Hét szerkesztés ellenőrzése (plan értékek)
    const editingWeek = this.currentEditingWeek;
    const editingDayIndices = this.currentEditingDayIndices;
    
    return editingWeek !== null &&
           editingWeek.metricIndices.includes(metricIndex) &&
           editingWeek.rowType === rowType &&
           editingDayIndices.includes(dayIndex);
  }

  /**
   * Hét szerkesztésének indítása
   */
  startWeekEditing(
    metricIndex: number,
    rowType: 'actual' | 'plan',
    dayIndex: number,
    isKumMode: boolean,
    metrics: PerformanceMetric[],
    weekNumbers: number[],
    isWeekendFn: (dayIndex: number) => boolean
  ): boolean {
    const metricName = metrics[metricIndex]?.name;
    
    if (isKumMode) {
      // KÜM módban minden metrika szerkeszthető plan soraiban
      if (rowType !== 'plan') {
        return false;
      }
    } else {
      // Call Center módban "Indított hívás", "Egyeztetett időpont" és "Vagyonra egyeztetett" P (plan) rows
      if ((metricName !== 'Indított hívás' && metricName !== 'Egyeztetett időpont' && metricName !== 'Vagyonra egyeztetett') || rowType !== 'plan') {
        return false;
      }
    }

    // Hétvégén nincs szerkesztés
    if (isWeekendFn(dayIndex)) {
      return false;
    }

    // Hétvégés számának meghatározása
    const weekNumber = weekNumbers[dayIndex];
    
    // Ha már ugyanazt a hetet szerkesztjük, ne indítsuk újra
    const currentEditingWeek = this.currentEditingWeek;
    if (currentEditingWeek && 
        currentEditingWeek.rowType === rowType && 
        currentEditingWeek.weekNumber === weekNumber) {
      return false;
    }
    
    // Ha másik hetet szerkesztünk, először mentsük el a jelenlegi változtatásokat
    if (currentEditingWeek) {
      // Itt lehetne egy callback a mentéshez, de most csak visszaadjuk, hogy mentés szükséges
      return false; // Jelzi, hogy előbb menteni kell
    }
    
    // Keressük meg az összes napot, ami ehhez a héthez tartozik (hétvégék nélkül)
    const weekDayIndices = weekNumbers
      .map((week, index) => week === weekNumber ? index : -1)
      .filter(index => index !== -1 && !isWeekendFn(index)); // Hétvégék kizárása
    
    let metricIndices: number[] = [];
    
    if (isKumMode) {
      // KÜM módban az összes metrikai index
      metricIndices = metrics.map((_, index) => index);
    } else {
      // Call Center módban "Indított hívás", "Egyeztetett időpont" és "Vagyonra egyeztetett" metrics
      const indítvásIndex = metrics.findIndex(m => m.name === 'Indított hívás');
      const egyeztetésIndex = metrics.findIndex(m => m.name === 'Egyeztetett időpont');
      const vagyonIndex = metrics.findIndex(m => m.name === 'Vagyonra egyeztetett');
      metricIndices = [indítvásIndex, egyeztetésIndex, vagyonIndex].filter(index => index !== -1);
    }
    
    // Szerkesztési állapot beállítása mindkét metrikához
    const editingWeek: EditingWeek = { metricIndices, rowType, weekNumber };
    this._editingWeek.next(editingWeek);
    this._editingDayIndices.next(weekDayIndices);
    
    // Jelenlegi plan értékek másolása a szerkesztő objektumba mindkét metrikához
    const editingValues: {[metricIndex: number]: number[]} = {};
    metricIndices.forEach(index => {
      editingValues[index] = weekDayIndices.map(dayIdx => metrics[index].planValues[dayIdx] || 0);
    });
    this._editingValues.next(editingValues);
    
    return true;
  }

  /**
   * Egyes cella szerkesztésének indítása (actual értékekhez)
   */
  startCellEditing(
    metricIndex: number,
    rowType: 'actual' | 'plan',
    dayIndex: number,
    isKumMode: boolean,
    metrics: PerformanceMetric[],
    isWeekendFn: (dayIndex: number) => boolean
  ): boolean {
    // Csak actual soroknál és munkanapokon engedélyezett (CC és KÜM módban egyaránt)
    if (rowType !== 'actual' || isWeekendFn(dayIndex)) {
      return false;
    }
    // CC módban csak a szerkeszthető metrikáknál
    if (!isKumMode && !this.isEditableCell(metricIndex, rowType, isKumMode, metrics)) {
      return false;
    }

    // Jelenlegi szerkesztések lemondása
    this.cancelAllEditing();

    // Egyes cella szerkesztési állapot beállítása
    const editingCell: EditingCell = { metricIndex, rowType, dayIndex };
    this._editingCell.next(editingCell);
    this._singleCellEditingValue.next(metrics[metricIndex].actualValues[dayIndex] || 0);

    return true;
  }

  /**
   * Szerkesztési értékek frissítése
   */
  updateEditingValue(metricIndex: number, dayIndex: number, value: number): void {
    const editingDayIndices = this.currentEditingDayIndices;
    const editIndex = editingDayIndices.indexOf(dayIndex);
    const currentEditingValues = {...this.currentEditingValues};
    
    if (currentEditingValues[metricIndex] && editIndex >= 0) {
      currentEditingValues[metricIndex][editIndex] = value;
      this._editingValues.next(currentEditingValues);
    }
  }

  /**
   * Egyes cella szerkesztési érték frissítése
   */
  updateSingleCellEditingValue(metricIndex: number, dayIndex: number, value: number): void {
    const editingCell = this.currentEditingCell;
    if (editingCell &&
        editingCell.metricIndex === metricIndex &&
        editingCell.dayIndex === dayIndex) {
      this._singleCellEditingValue.next(value);
    }
  }

  /**
   * Szerkesztési érték lekérése
   */
  getEditingValue(metricIndex: number, dayIndex: number): number {
    const editingDayIndices = this.currentEditingDayIndices;
    const editIndex = editingDayIndices.indexOf(dayIndex);
    const editingValues = this.currentEditingValues;
    return editingValues[metricIndex]?.[editIndex] || 0;
  }

  /**
   * Egyes cella szerkesztési érték lekérése
   */
  getSingleCellEditingValue(metricIndex: number, dayIndex: number, metrics: PerformanceMetric[]): number {
    const editingCell = this.currentEditingCell;
    if (editingCell &&
        editingCell.metricIndex === metricIndex &&
        editingCell.dayIndex === dayIndex) {
      return this.currentSingleCellEditingValue;
    }
    return metrics[metricIndex].actualValues[dayIndex] || 0;
  }

  /**
   * Szerkesztési index lekérése
   */
  getEditingValueIndex(dayIndex: number): number {
    const editingDayIndices = this.currentEditingDayIndices;
    return editingDayIndices.indexOf(dayIndex);
  }

  /**
   * Minden szerkesztés lemondása
   */
  cancelAllEditing(): void {
    this._editingWeek.next(null);
    this._editingCell.next(null);
    this._editingValues.next({});
    this._editingDayIndices.next([]);
    this._singleCellEditingValue.next(0);
  }

  /**
   * Hét szerkesztés lemondása
   */
  cancelWeekEditing(): void {
    this._editingWeek.next(null);
    this._editingValues.next({});
    this._editingDayIndices.next([]);
  }

  /**
   * Egyes cella szerkesztés lemondása
   */
  cancelCellEditing(): void {
    this._editingCell.next(null);
    this._singleCellEditingValue.next(0);
  }

  /**
   * Szerkesztés állapotának alaphelyzetbe állítása
   */
  reset(): void {
    this.cancelAllEditing();
  }
}